// src/pages/admin/library/MemberManagement.tsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  FaSearch, 
  FaPrint, 
  FaPlus, 
  FaEdit,
  FaTrash,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCheck,
  FaSync,
  FaFilter
} from '../../../utils/iconUtils';
import { useNavigate } from 'react-router-dom';
import { 
  LibraryMember, 
  getLibraryMembers, 
  deleteLibraryMember,
  getLibraryStatistics,
  getLibraryMemberByMemId,
  createLibraryMember,
  updateLibraryMember,
  uploadMemberPhoto,
  deleteMemberPhoto
} from '../../../firebase/libraryFirestoreService';
import AddMemberModal from '../../../pages/admin/library/AddMemberModal';
import { useUI } from '../../../components/common/UIMessageProvider';
import { useAuth } from '../../../contexts/AuthContext';
import { cache } from '../../../utils/cacheManager';

// Cache configuration
const CACHE_VERSION = '1.2.0'; // Version for schema changes
const MEMBERS_CACHE_KEY = `library.members.${CACHE_VERSION}`;
const MEMBERS_CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes
const MEMBERS_LIST_LIMIT = 2000;

// Helper functions for local dates (fix timezone issues)
const getLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateStr: string): Date => {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const MemberManagement: React.FC = () => {
  const navigate = useNavigate();
  const ui = useUI();
  const auth = useAuth();
  // Permission checks for library actions
  const canViewLibrary = auth.canPerformAction('library', 'view');
  const canAddLibrary = auth.canPerformAction('library', 'create');
  const canEditLibrary = auth.canPerformAction('library', 'edit');
  const canDeleteLibrary = auth.canPerformAction('library', 'delete');
  const canPrintLibrary = auth.canPerformAction('library', 'print') || auth.canPerformAction('library', 'export');
  const canExportLibrary = auth.canPerformAction('library', 'export');
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [members, setMembers] = useState<LibraryMember[]>([]);
  const [allMembers, setAllMembers] = useState<LibraryMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<LibraryMember | null>(null);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    totalBooks: 0,
    availableBooks: 0,
    todayAttendance: 0,
    totalBorrowings: 0
  });

  const reportRef = useRef<HTMLDivElement | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);

  // Cache helpers replaced with centralized cache
  const invalidateCache = useCallback(() => {
    try {
      cache.invalidate(MEMBERS_CACHE_KEY);
      // Broadcast to other tabs (keep existing event name for compatibility)
      window.dispatchEvent(new Event('membersCacheInvalidated'));
      return true;
    } catch (error) {
      console.warn('Failed to invalidate cache:', error);
      return false;
    }
  }, []);

  // Lightweight helpers to inspect persisted cache state and read stale data
  const isCacheValid = useCallback((): boolean => {
    try {
      const state = cache.getCacheState(MEMBERS_CACHE_KEY);
      return state.hasCache && state.isFresh;
    } catch (e) {
      return false;
    }
  }, []);

  const getCachedMembers = useCallback((): LibraryMember[] => {
    try {
      const raw = localStorage.getItem('app_cache_v1');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const entry = parsed[MEMBERS_CACHE_KEY];
      if (entry && entry.data) return entry.data as LibraryMember[];
    } catch (e) {
      // ignore parse errors
    }
    return [];
  }, []);

  // Apply filters to cached data
  const applyFiltersToCachedData = useCallback(() => {
    if (allMembers.length === 0) return;

    let filtered = [...allMembers];

    // Apply gender filter
    if (genderFilter !== 'all') {
      filtered = filtered.filter(member => 
        member.gender && member.gender.toLowerCase() === genderFilter.toLowerCase()
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(member => 
        member.status && member.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(member => {
        const nameMatch = member.fullName?.toLowerCase().includes(searchLower) || false;
        const idMatch = member.memId?.toLowerCase().includes(searchLower) || false;
        const contactMatch = member.contact ? 
          (typeof member.contact === 'string' ? member.contact.toLowerCase().includes(searchLower) : false) : 
          false;
        
        return nameMatch || idMatch || contactMatch;
      });
    }

    // Sort by memId descending
    const sorted = filtered.sort((a, b) => {
      const aNum = parseInt((a.memId || '').match(/\d+/)?.[0] || '0', 10);
      const bNum = parseInt((b.memId || '').match(/\d+/)?.[0] || '0', 10);
      if (!isNaN(aNum) && !isNaN(bNum) && (aNum || bNum)) {
        return bNum - aNum;
      }
      return (b.memId || '').localeCompare(a.memId || '');
    });

    setMembers(sorted);
    setCurrentPage(1);
  }, [allMembers, genderFilter, statusFilter, searchTerm]);

  // Generate unique memId (fixes race condition)
  const generateUniqueMemId = useCallback(() => {
    // Get all existing memIds
    const existingIds = allMembers.map(m => m.memId || '');
    
    // Find the highest numeric ID
    let maxNum = 0;
    existingIds.forEach(id => {
      const match = id.match(/^M(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    
    // Generate new ID with timestamp to ensure uniqueness
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 100);
    return `M${(maxNum + 1).toString().padStart(3, '0')}_${timestamp}${random}`;
  }, [allMembers]);

  // Fetch members with cache
  const fetchMembers = useCallback(async (isBackgroundRefresh = false) => {
    if (isBackgroundRefresh) {
      setBackgroundRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      // Use centralized cache; cache.get will return cached data if fresh
      const data = await cache.get(MEMBERS_CACHE_KEY, async () => {
        const result = await getLibraryMembers({ limitCount: MEMBERS_LIST_LIMIT });
        if (!result.success) throw new Error(result.error || 'Failed to fetch members');
        return result.data;
      }, MEMBERS_CACHE_EXPIRY);

      setAllMembers(data);
      if (!isBackgroundRefresh) setMembers(data);
      console.log(isBackgroundRefresh ? '🔄 Background refresh completed' : '🔄 Fetched from Firebase and cached');
      return data;
    } catch (error) {
      console.error('Error fetching members:', error);
      if (!isBackgroundRefresh) {
        // Try to fall back to persisted cache in localStorage (stale)
        try {
          const raw = localStorage.getItem('app_cache_v1');
          if (raw) {
            const parsed = JSON.parse(raw);
            const entry = parsed[MEMBERS_CACHE_KEY];
            if (entry && entry.data) {
              setAllMembers(entry.data);
              setMembers(entry.data);
              ui.showMessage('Using cached data (offline mode)', { type: 'info' });
              return entry.data;
            }
          }
        } catch (e) {
          // ignore
        }
      }
      return [];
    } finally {
      if (isBackgroundRefresh) {
        setBackgroundRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [ui]);

  // Background refresh function
  const backgroundRefresh = useCallback(async () => {
    if (backgroundRefreshing) return;
    // Only refresh if cache is older than 30 seconds
    try {
      const state = cache.getCacheState(MEMBERS_CACHE_KEY);
      if (state.hasCache && state.age != null && state.age < 30 * 1000) {
        return; // Cache is fresh enough
      }
    } catch (e) {
      // ignore and proceed to fetch
    }

    await fetchMembers(true);
    // After background refresh, reapply filters
    applyFiltersToCachedData();
  }, [backgroundRefreshing, isCacheValid, fetchMembers, applyFiltersToCachedData]);

  const fetchStatistics = async () => {
    try {
      const result = await getLibraryStatistics();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Handle storage events for multi-tab sync
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'app_cache_v1' || e.key === MEMBERS_CACHE_KEY) {
        console.log('🔄 Storage event detected, refreshing from cache');
        const cachedMembers = getCachedMembers();
        setAllMembers(cachedMembers);
        if (!backgroundRefreshing) setMembers(cachedMembers);
      }
    };

    const handleCacheInvalidated = () => {
      console.log('🔄 Cache invalidation event received');
      fetchMembers(false);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('membersCacheInvalidated', handleCacheInvalidated);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('membersCacheInvalidated', handleCacheInvalidated);
    };
  }, [fetchMembers, getCachedMembers]);

  // Background refresh on tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        backgroundRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Periodic refresh every 2 minutes
    const interval = setInterval(() => {
      if (document.hasFocus()) {
        backgroundRefresh();
      }
    }, 2 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [backgroundRefresh]);

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchMembers(false);
    fetchStatistics();
  }, [fetchMembers]);

  // Apply filters when allMembers or filters change
  useEffect(() => {
    applyFiltersToCachedData();
  }, [allMembers, genderFilter, statusFilter, searchTerm, applyFiltersToCachedData]);

  // Keep current page in bounds
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(members.length / itemsPerPage));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [members.length, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    // No need to fetch from Firebase - just apply search to cached data
  };

  const handleDeleteMember = async (memberId: string) => {
    const ok = await ui.askConfirm({
      title: 'Delete Member',
      message: 'Are you sure you want to delete this member?'
    });
    if (!ok) return;

    try {
      const result = await deleteLibraryMember(memberId);
      if (result.success) {
        ui.showMessage('Member deleted successfully!');
        // Invalidate cache and refetch
        invalidateCache();
        fetchMembers(false);
        fetchStatistics();
      } else {
        ui.showMessage(`Error: ${result.error}`, { type: 'error' });
      }
    } catch (error) {
      ui.showMessage('Error deleting member. Please try again.', { type: 'error' });
    }
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setShowAddModal(true);
  };

  const handleEditClick = (member: LibraryMember) => {
    setEditingMember(member);
    setShowAddModal(true);
  };

  const handleSaveMember = async (formData: any) => {
    setLoading(true);
    try {
      // Generate or use memId
      const memId = String(formData.memberId || editingMember?.memId || generateUniqueMemId()).trim().toUpperCase();
      if (!memId) {
        throw new Error('Member ID is required');
      }

      const changedMemId = !editingMember || memId !== String(editingMember.memId || '').trim().toUpperCase();
      if (changedMemId) {
        const existingMember = await getLibraryMemberByMemId(memId);
        if (existingMember.success && existingMember.data?.id && existingMember.data.id !== editingMember?.id) {
          throw new Error(`Member ID ${memId} already exists`);
        }
      }
      
      // Map form data to Firestore structure
      const memberPayload: LibraryMember = {
        memId,
        fullName: formData.fullName,
        dob: formData.dateOfBirth,
        gender: formData.gender as 'Male' | 'Female' | 'Other',
        registrationDate: formData.registrationDate || getLocalDateString(new Date()),
        contact: formData.contact,
        address: formData.address,
        status: 'Active',
        originalPhotoPath: editingMember?.originalPhotoPath || undefined,
        photoUrl: editingMember?.photoUrl || undefined,
      };

      // Handle photo upload
      if (formData.profilePhoto) {
        const uploadResult = await uploadMemberPhoto(
          formData.profilePhoto, 
          memId,
          editingMember?.originalPhotoPath
        );
        
        if (uploadResult.success && uploadResult.url && uploadResult.path) {
          memberPayload.photoUrl = uploadResult.url;
          memberPayload.originalPhotoPath = uploadResult.path;
        } else if (uploadResult.error) {
          console.warn('Photo upload failed:', uploadResult.error);
        }
      } else if (formData.removePhoto) {
        if (editingMember?.originalPhotoPath) {
          await deleteMemberPhoto(editingMember.originalPhotoPath);
        }
        memberPayload.photoUrl = undefined;
        memberPayload.originalPhotoPath = undefined;
      } else if (editingMember) {
        memberPayload.photoUrl = editingMember.photoUrl;
        memberPayload.originalPhotoPath = editingMember.originalPhotoPath;
      }

      if (editingMember && editingMember.id) {
        // Update existing member
        const res = await updateLibraryMember(editingMember.id, memberPayload);
        if (!res.success) throw new Error(res.error || 'Update failed');
        ui.showMessage('Member updated successfully');
      } else {
        // Create new member
        const res = await createLibraryMember(memberPayload);
        if (!res.success) throw new Error(res.error || 'Create failed');
        ui.showMessage('Member created successfully');
      }

      // Invalidate cache and refetch
      invalidateCache();
      await fetchMembers(false);
      await fetchStatistics();
      setShowAddModal(false);
      setEditingMember(null);
    } catch (error: any) {
      console.error('Save error:', error);
      ui.showMessage(`Error saving member: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // keep legacy quick-print (not used when template print is available)
    window.print();
  };

  const exportToPDF = async () => {
    if (!members.length) return;
    if (!reportRef.current) return;
    try {
      ui.showMessage('Generating PDF...');
      const html2canvasModule: any = await import('html2canvas');
      const html2canvas = html2canvasModule.default || html2canvasModule;
      const jspdfModule: any = await import('jspdf');
      const { jsPDF } = jspdfModule;

      const canvas = await html2canvas(reportRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      const fileName = `members-report-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      ui.showMessage('PDF generated successfully.');
    } catch (err) {
      console.error('exportToPDF error', err);
      ui.showMessage('Failed to generate PDF.', { type: 'error' });
    }
  };

  const handlePrintTemplate = () => {
    if (!members.length) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const accent = '#486581';

    const statsHtml = `
      <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
        <tbody>
          <tr><td style="font-weight:600;color:#374151;padding:6px 8px;">Total</td><td style="text-align:right;font-weight:700;padding:6px 8px;">${filteredStats.total}</td></tr>
          <tr><td style="font-weight:600;color:#374151;padding:6px 8px;">Active</td><td style="text-align:right;font-weight:700;padding:6px 8px;">${filteredStats.active}</td></tr>
          <tr><td style="font-weight:600;color:#374151;padding:6px 8px;">Male</td><td style="text-align:right;font-weight:700;padding:6px 8px;">${filteredStats.male}</td></tr>
          <tr><td style="font-weight:600;color:#374151;padding:6px 8px;">Female</td><td style="text-align:right;font-weight:700;padding:6px 8px;">${filteredStats.female}</td></tr>
        </tbody>
      </table>
    `;

    const rows = members.map((m, i) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eaeaea;">${i + 1}</td>
        <td style="padding:8px;border-bottom:1px solid #eaeaea;font-family:monospace;">${m.memId || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eaeaea;">${m.fullName || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eaeaea;">${m.dob ? new Date(m.dob).toLocaleDateString() : 'N/A'}</td>
        <td style="padding:8px;border-bottom:1px solid #eaeaea;">${m.gender || '-'}</td>
        <td style="padding:8px;border-bottom:1px solid #eaeaea;">${m.registrationDate ? new Date(m.registrationDate).toLocaleDateString() : 'N/A'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Members Report</title>
        <meta charset="utf-8" />
        <style>
          *{box-sizing:border-box;margin:0;padding:0}
          :root{--accent:${accent}}
          body{font-family:Segoe UI, Tahoma, Geneva, Verdana, sans-serif;padding:20px;color:#111827}
          .header{background:var(--accent);color:#fff;padding:18px;border-radius:6px 6px 0 0}
          .org{font-weight:600;font-size:13px;color:rgba(255,255,255,0.95);margin-bottom:6px}
          .title{font-size:18px;font-weight:700}
          .meta{font-size:12px;color:rgba(255,255,255,0.95);margin-top:4px}
          .container{border:1px solid #e5e7eb;border-radius:6px;overflow:hidden}
          thead{background:var(--accent);color:#fff}
          th{padding:10px;text-align:left;font-weight:700}
          td{padding:8px;border-bottom:1px solid #eaeaea}
          .footer{padding:12px 16px;display:flex;justify-content:space-between;align-items:center;font-size:11px;color:#6b7280}
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="org">Alhaji Inusah Foundation</div>
            <div class="title">Members Report</div>
            <div class="meta">Generated on ${new Date().toLocaleString()}</div>
          </div>
          <div style="padding:16px">${statsHtml}</div>
          <div style="padding:0 16px 16px 16px;overflow:auto">
            <table style="width:100%;border-collapse:collapse;font-size:12px">
              <thead>
                <tr>
                  <th style="width:40px">#</th>
                  <th>Mem ID</th>
                  <th>Name</th>
                  <th>DOB</th>
                  <th>Gender</th>
                  <th>Reg Date</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>
          </div>
          <div class="footer">
            <div>Total Members: ${members.length}</div>
            <div>© ${new Date().getFullYear()} Alhaji Inusah Foundation. All rights reserved.</div>
          </div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => printWindow.print(), 600);
  };

  const handleRefresh = () => {
    // Force refresh by invalidating cache
    invalidateCache();
    fetchMembers(false);
  };

  const getStatusBadge = (status: string) => {
    const s = (status || '').toString().trim().toLowerCase();
    if (s === 'active') {
      return (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center space-x-1">
          <FaCheckCircle className="h-3 w-3" />
          <span>Active</span>
        </span>
      );
    }
    if (s === 'semi-active' || s === 'semi active' || s === 'semi') {
      return (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 flex items-center space-x-1">
          <FaUserCheck className="h-3 w-3" />
          <span>Semi-active</span>
        </span>
      );
    }
    return (
      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
        Inactive
      </span>
    );
  };

  // Pagination helpers
  const totalPages = Math.max(1, Math.ceil(members.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedMembers = members.slice(startIndex, endIndex);

  // Cache status
  const cacheStatus = useMemo(() => {
    if (loading) return 'Loading...';
    if (backgroundRefreshing) return '🔄 Background refreshing...';
    if (isCacheValid()) return '📚 Using cached data';
    return '🔄 Online data';
  }, [loading, backgroundRefreshing, isCacheValid]);

  // Calculate total stats from filtered members
  const filteredStats = useMemo(() => {
    const activeMembers = members.filter(m => m.status?.toLowerCase() === 'active').length;
    const maleMembers = members.filter(m => m.gender === 'Male').length;
    const femaleMembers = members.filter(m => m.gender === 'Female').length;
    
    return {
      total: members.length,
      active: activeMembers,
      male: maleMembers,
      female: femaleMembers
    };
  }, [members]);

return (
  <div ref={reportRef} className="space-y-6">
    {/* Add Member Modal */}
    {showAddModal && (
      <AddMemberModal
        isOpen={showAddModal}
        initialData={editingMember ? {
          memberId: editingMember.memId ?? '',
          fullName: editingMember.fullName ?? '',
          dateOfBirth: editingMember.dob ?? '',
          gender: editingMember.gender ?? 'Male',
          registrationDate: editingMember.registrationDate ?? '',
          contact: editingMember.contact ?? '',
          address: editingMember.address ?? '',
          photoUrl: editingMember.photoUrl ?? undefined,
        } : undefined}
        onClose={() => { setShowAddModal(false); setEditingMember(null); }}
        onSave={handleSaveMember}
      />
    )}

    {/* Top Controls */}
    <div className="bg-white rounded-xl shadow-soft p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Left: Statistics & Cache Status */}
        <div className="flex flex-wrap gap-4 items-end">
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-blue-50 p-2 rounded-lg">
              <div className="text-lg font-bold text-blue-600">{filteredStats.total}</div>
              <div className="text-xs text-gray-600">Total</div>
            </div>
            <div className="bg-green-50 p-2 rounded-lg">
              <div className="text-lg font-bold text-green-600">{filteredStats.active}</div>
              <div className="text-xs text-gray-600">Active</div>
            </div>
            <div className="bg-blue-50 p-2 rounded-lg">
              <div className="text-lg font-bold text-blue-600">{filteredStats.male}</div>
              <div className="text-xs text-gray-600">Male</div>
            </div>
            <div className="bg-pink-50 p-2 rounded-lg">
              <div className="text-lg font-bold text-pink-600">{filteredStats.female}</div>
              <div className="text-xs text-gray-600">Female</div>
            </div>
          </div>
          
        
        </div>

        {/* Right: Controls */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={genderFilter}
              onChange={(e) => { setGenderFilter(e.target.value); setCurrentPage(1); }}
              className="text-sm px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="text-sm px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Semi-active">Semi-active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center">
            <div className="relative">
              <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-sm pl-8 pr-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-40 sm:w-56"
              />
            </div>
            <button
              type="submit"
              className="ml-2 text-sm px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              title="Search"
            >
              Search
            </button>
          </form>

          {/* Action Buttons */}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title="Refresh from Firebase"
          >
            <FaSync className={`h-3 w-3 ${backgroundRefreshing ? 'animate-spin' : 'text-gray-600'}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          {canPrintLibrary && (
            <div className="flex items-center gap-2">
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                title="Export PDF"
              >
                <FaPrint className="h-3 w-3 text-gray-600" />
                <span className="hidden sm:inline">PDF</span>
              </button>
              <button
                onClick={handlePrintTemplate}
                className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                title="Print"
              >
                <FaPrint className="h-3 w-3 text-gray-600" />
                <span className="hidden sm:inline">Print</span>
              </button>
            </div>
          )}

          {canAddLibrary && (
            <button
              onClick={handleAddMember}
              className="flex items-center gap-2 text-sm px-3 py-1 rounded-md transition-colors bg-primary-600 text-white hover:bg-primary-700"
              title="Add New Member"
            >
              <FaPlus className="h-3 w-3" />
              <span className="hidden sm:inline">Add</span>
            </button>
          )}
        </div>
      </div>

      {/* Cache Info Banner */}
      <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded">
        <div className="flex items-center justify-between">
          <span>
            <strong>Cache Info:</strong> {allMembers.length} members cached • 
            Last refresh: {(() => {
              try {
                const cacheData = localStorage.getItem(MEMBERS_CACHE_KEY);
                if (cacheData) {
                  const { timestamp } = JSON.parse(cacheData);
                  const minutesAgo = Math.floor((Date.now() - timestamp) / (60 * 1000));
                  return minutesAgo === 0 ? 'just now' : `${minutesAgo} minutes ago`;
                }
              } catch (e) {}
              return 'unknown';
            })()}
          </span>
            {/* Cache Status */}
          <div className="text-xs text-gray-500 hidden sm:block">
            {cacheStatus} {backgroundRefreshing && '(background)'}
          </div>
          <span className="text-xs px-2 py-0.5 bg-gray-200 rounded">
            v{CACHE_VERSION}
          </span>
          
        </div>
      </div>
    </div>

    {/* ======================== DATA GRID SECTION ======================== */}
    <div className="bg-white rounded-xl shadow-soft overflow-hidden">
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-3 text-sm text-gray-600">Loading members...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs leading-tight">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Mem. ID</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Gender</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Reg. Date</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedMembers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-2 py-6 text-center text-gray-500 text-sm">
                      {allMembers.length === 0 
                        ? 'No members found. Click "Add New Member" to add your first member.' 
                        : 'No members match your search criteria.'}
                    </td>
                  </tr>
                ) : (
                  displayedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2 whitespace-nowrap font-medium text-primary-600">
                        {member.memId}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-900">{member.fullName}</div>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500">
                        {member.dob ? new Date(member.dob).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-[11px] font-medium ${
                          member.gender === 'Male' 
                            ? 'bg-blue-100 text-blue-800'
                            : member.gender === 'Female'
                            ? 'bg-pink-100 text-pink-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.gender}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500">
                        {member.registrationDate ? new Date(member.registrationDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500">
                        {member.contact || 'N/A'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500 max-w-[200px] truncate">
                        {member.address || 'N/A'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap w-28">
                        {getStatusBadge(member.status)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs font-medium w-24">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => navigate(`/admin/library/members/view/${member.id}`)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="View"
                          >
                            <FaEye className="h-3 w-3" />
                          </button>
                          {canEditLibrary && (
                            <button
                              onClick={() => handleEditClick(member)}
                              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                              title="Edit"
                            >
                              <FaEdit className="h-3 w-3" />
                            </button>
                          )}
                          {canDeleteLibrary && (
                            <button
                              onClick={() => handleDeleteMember(member.id!)}
                              className="p-1 rounded text-red-600 hover:text-red-900 hover:bg-red-50"
                              title="Delete"
                            >
                              <FaTrash className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ======================== PAGINATION SECTION ======================== */}
          <div className="px-3 py-2 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-700">
              <div>
                Showing <span className="font-medium">{members.length === 0 ? 0 : startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(endIndex, members.length)}</span> of{' '}
                <span className="font-medium">{members.length}</span> members
                <span className="ml-2 text-gray-500">(Total: {allMembers.length})</span>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                    currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Prev
                </button>

                {/* Simple numeric pagination: show up to 5 pages centered on current */}
                {Array.from({ length: totalPages }).map((_, idx) => {
                  const page = idx + 1;
                  // Limit pages shown
                  if (totalPages > 7) {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-2 py-1 rounded text-xs ${
                            currentPage === page
                              ? 'bg-primary-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                    if (page === 2 && currentPage > 3) {
                      return <span key={'dots-left'} className="px-2">...</span>;
                    }
                    if (page === totalPages - 1 && currentPage < totalPages - 2) {
                      return <span key={'dots-right'} className="px-2">...</span>;
                    }
                    return null;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-2 py-1 rounded text-xs ${
                        currentPage === page
                          ? 'bg-primary-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-2 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50 ${
                    currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  </div>
);
};

export default MemberManagement;
